import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { format, formatDistance } from 'date-fns';
import {
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

interface Delisting {
  id: string;
  coinName: string | null;
  coinSymbol: string;
  coinIcon: string | null;
  announcementDateTime: string;
  delistingDateTime: string;
  announcementLink: string;
}

type SortDirection = 'asc' | 'desc' | null;
type SortField = 'coinName' | 'coinSymbol' | 'announcementDateTime' | 'delistingDateTime' | null;

const Delistings: React.FC = () => {
  const [delistings, setDelistings] = useState<Delisting[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 20, 50, 100];

  useEffect(() => {
    const fetchDelistings = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_DELISTINGS_URL || 'http://localhost:5001/api/delistings'}`
        );
        const data = response.data;

        // Check for missing icons and fetch from Binance API if needed
        const cachedSymbols = JSON.parse(localStorage.getItem('binanceSymbols') || 'null');
        const symbols = cachedSymbols || (await fetchBinanceSymbols());

        const updatedDelistings = data.map((delisting: Delisting) => {
          if (!delisting.coinIcon || !delisting.coinName) {
            const symbolData = symbols.find(
              (item: any) =>
                item.symbol === delisting.coinSymbol ||
                item.symbol.toUpperCase() === delisting.coinSymbol
            );
            if (symbolData) {
              if (!delisting.coinIcon) delisting.coinIcon = symbolData.baseAssetLogo || null;
              if (!delisting.coinName) delisting.coinName = symbolData.baseAssetName || null;
            }
          }
          return delisting;
        });

        setDelistings(updatedDelistings);
      } catch (error) {
        console.error('Error fetching delistings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDelistings();
  }, []);

  const fetchBinanceSymbols = async () => {
    try {
      const response = await axios.get(
        'https://www.binance.com/bapi/composite/v1/public/marketing/symbol/list'
      );
      // Check if response has the expected structure
      if (response?.data?.data && Array.isArray(response.data.data)) {
        const symbols = response.data.data;
        localStorage.setItem('binanceSymbols', JSON.stringify(symbols)); // Cache the symbols in local storage
        return symbols;
      } else {
        console.error('Unexpected response format from Binance API:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching Binance symbols:', error);
      return [];
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field is clicked
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      // Set new sort field and direction to asc
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />;
    if (sortDirection === 'asc') return <ArrowUpIcon className="h-4 w-4 text-blue-500" />;
    if (sortDirection === 'desc') return <ArrowDownIcon className="h-4 w-4 text-blue-500" />;
    return null;
  };

  // Filter and sort delistings
  const filteredAndSortedDelistings = useMemo(() => {
    // First filter based on search term
    let result = delistings.filter(delisting => {
      const searchTermLower = searchTerm.toLowerCase();
      return (
        (delisting.coinName?.toLowerCase() || '').includes(searchTermLower) ||
        delisting.coinSymbol.toLowerCase().includes(searchTermLower)
      );
    });

    // Then sort if sort field is set
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: any = a[sortField as keyof Delisting];
        let bValue: any = b[sortField as keyof Delisting];

        if (sortField.includes('DateTime')) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        if (aValue === null) return sortDirection === 'asc' ? 1 : -1;
        if (bValue === null) return sortDirection === 'asc' ? -1 : 1;

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [delistings, searchTerm, sortField, sortDirection]);

  // Pagination logic
  const paginatedDelistings = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedDelistings.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedDelistings, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredAndSortedDelistings.length / pageSize);
  }, [filteredAndSortedDelistings, pageSize]);

  // Reset to page 1 when filtering or changing page size
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  // Pagination controls
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
  };

  // Safe date formatting with error handling
  const formatSafeDate = (dateString: string, formatString: string): string => {
    try {
      return format(new Date(dateString), formatString);
    } catch (error) {
      console.error(`Error formatting date: ${dateString}`, error);
      return 'Invalid date';
    }
  };

  // Get time until delisting with error handling
  const getTimeUntilDelisting = (delistingDateTime: string) => {
    try {
      const delistingDate = new Date(delistingDateTime);

      // Check if date is valid
      if (isNaN(delistingDate.getTime())) {
        return <span className="text-gray-500">Unknown</span>;
      }

      const now = new Date();

      if (delistingDate < now) {
        return <span className="text-red-500 font-medium">Delisted</span>;
      }

      return (
        <span className="text-yellow-500 font-medium">
          {formatDistance(delistingDate, now, { addSuffix: true })}
        </span>
      );
    } catch (error) {
      console.error(`Error calculating time until delisting for: ${delistingDateTime}`, error);
      return <span className="text-gray-500">Unknown</span>;
    }
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <div className="animate-pulse">
      <div className="bg-gray-200 dark:bg-gray-700 h-10 w-64 mb-4 rounded"></div>
      <div className="space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="grid grid-cols-6 gap-4">
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded col-span-1"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded col-span-1"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded col-span-1"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded col-span-1"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded col-span-1"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded col-span-1"></div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Delisted Coins</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Track coins that are being delisted from Binance. Stay informed to protect your
            investments.
          </p>

          {/* Search input stays the same */}
          <div className="relative mb-6">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                        bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search by coin name or symbol..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            renderSkeleton()
          ) : (
            <div className="relative w-full">
              <div className="overflow-x-auto rounded-lg">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                  <colgroup>
                    <col className="w-[5%]" /> {/* Icon column */}
                    <col className="w-[20%]" /> {/* Name column */}
                    <col className="w-[10%]" /> {/* Symbol column */}
                    <col className="w-[20%]" /> {/* Announcement Date column */}
                    <col className="w-[20%]" /> {/* Delisting Date column */}
                    <col className="w-[17%]" /> {/* Status column */}
                    <col className="w-[8%]" /> {/* Details column */}
                  </colgroup>
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th
                        scope="col"
                        className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                      >
                        Icon
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('coinName')}
                      >
                        <div className="flex items-center">
                          Name
                          <span className="ml-1">{getSortIcon('coinName')}</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('coinSymbol')}
                      >
                        <div className="flex items-center">
                          Symbol
                          <span className="ml-1">{getSortIcon('coinSymbol')}</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('announcementDateTime')}
                      >
                        <div className="flex items-center">
                          Announcement Date
                          <span className="ml-1">{getSortIcon('announcementDateTime')}</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('delistingDateTime')}
                      >
                        <div className="flex items-center">
                          Delisting Date
                          <span className="ml-1">{getSortIcon('delistingDateTime')}</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16"
                      >
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedDelistings.length > 0 ? (
                      paginatedDelistings.map(delisting => (
                        <tr
                          key={delisting.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          <td className="px-2 py-4 whitespace-nowrap text-center">
                            <img
                              src={delisting.coinIcon || 'https://via.placeholder.com/32'}
                              alt={delisting.coinSymbol}
                              className="w-6 h-6 rounded-full inline-block"
                              onError={e => {
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = 'https://via.placeholder.com/32';
                              }}
                            />
                          </td>
                          <td className="px-4 py-4 truncate">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {delisting.coinName || 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white font-mono">
                              {delisting.coinSymbol}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500 dark:text-gray-300">
                              {formatSafeDate(delisting.announcementDateTime, 'PPP')}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatSafeDate(delisting.announcementDateTime, 'p')}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500 dark:text-gray-300">
                              {formatSafeDate(delisting.delistingDateTime, 'PPP')}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatSafeDate(delisting.delistingDateTime, 'p')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getTimeUntilDelisting(delisting.delistingDateTime)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <a
                              href={delisting.announcementLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-400 transition inline-block"
                              title="View Announcement"
                            >
                              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-4 whitespace-nowrap text-center text-gray-500 dark:text-gray-400"
                        >
                          No delistings found matching your search
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              {filteredAndSortedDelistings.length > 0 && (
                <div className="flex items-center justify-between mt-6 px-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                    <span>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {pageSizeOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center space-x-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {`${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, filteredAndSortedDelistings.length)} of ${filteredAndSortedDelistings.length}`}
                    </span>
                    <div className="flex items-center">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className={`p-1 rounded-full ${
                          currentPage === 1
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        aria-label="Previous page"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className={`p-1 rounded-full ${
                          currentPage === totalPages
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        aria-label="Next page"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Delistings;
